// <div x-data x-marquee.speed.80.direction.1.pause.inertia>
//   <div>
//     ...children
//   </div>
// </div>

import { Marquee as MarqueeClass } from '@joycostudio/marquee'

interface Params {
  value: string
  modifiers: Array<string>
  expression: string
}

interface DirectiveParams {
  Alpine: typeof Alpine
  effect: (callback: () => void) => void
  cleanup: (callback: () => void) => void
}

export function Marquee(
  el: HTMLElement,
  { modifiers }: Params,
  { cleanup }: DirectiveParams,
) {
  const children = el.children

  if (children.length === 0) return
  if (children.length > 1) {
    console.error(
      'Marquee does not support multiple children. Wrap it up in a single element.',
    )
  }

  const child = children[0] as HTMLElement
  if (!child) return

  let hasSpeed = modifiers.includes('speed')
  let hasDirection = modifiers.includes('direction')
  let pauseOnhover = modifiers.includes('pause')
  let hasInertia = modifiers.includes('inertia')

  const speed = hasSpeed ? modifierValue(modifiers, 'speed', 40) : 10
  const direction = hasDirection ? modifierValue(modifiers, 'direction', 1) : 1

  const { root, wrapper } = createRootElement()

  wrapper.appendChild(child)
  el.innerHTML = ''
  el.appendChild(root)

  const marq = new MarqueeClass(wrapper, {
    speed,
    speedFactor: 1,
    direction,
  })

  try {
    marq.initialize(child)
    marq.play()
  } catch (error) {
    console.error('Error during Marquee initialization:', error)
  }

  const cleanupInertia = hasInertia
    ? addScrollInertia(marq, speed, direction)
    : null

  if (pauseOnhover) {
    el.addEventListener('mouseenter', () => marq.pause())
    el.addEventListener('mouseleave', () => marq.play())
  }

  cleanup(() => {
    cleanupInertia?.()
    marq && marq.destroy()
  })
}

function createRootElement() {
  const root = document.createElement('div')
  root.style.overflowX = 'clip'
  root.style.overflowY = 'visible'
  root.style.maxWidth = '100%'

  const wrapper = document.createElement('div')
  wrapper.style.minWidth = 'max-content'
  wrapper.style.display = 'flex'

  root.appendChild(wrapper)

  return { root, wrapper }
}

function addScrollInertia(
  marquee: MarqueeClass,
  minSpeed: number,
  direction: number,
) {
  const lenis = window.lenis
  if (!lenis) return () => {}

  let currentFactor = 1
  let targetFactor = 1
  const maxVelocity = 5
  const lerpSpeed = 0.05
  let rafId: number | null = null

  function tick() {
    currentFactor += (targetFactor - currentFactor) * lerpSpeed
    // Snap to 1 when close enough to avoid perpetual micro-updates
    if (Math.abs(currentFactor - 1) < 0.001) currentFactor = 1
    marquee.setSpeedFactor(currentFactor * direction)

    // Decay target back toward 1 when not actively scrolling
    targetFactor += (1 - targetFactor) * lerpSpeed

    rafId = requestAnimationFrame(tick)
  }

  rafId = requestAnimationFrame(tick)

  const unsubscribe = lenis.on('scroll', (instance) => {
    const absVelocity = Math.abs(instance.velocity)
    targetFactor = 1 + (absVelocity / maxVelocity) * 3
  })

  return () => {
    unsubscribe()
    if (rafId !== null) cancelAnimationFrame(rafId)
  }
}

function modifierValue(modifiers: Array<string>, key: string, fallback: any) {
  if (modifiers.indexOf(key) === -1) return fallback

  // If it IS present, grab the value after it: x-marquee.speed.40
  const rawValue = modifiers[modifiers.indexOf(key) + 1]

  if (!rawValue) return fallback

  if (key === 'speed' || key === 'direction') {
    if (isNaN(parseInt(rawValue))) return fallback
    return parseInt(rawValue)
  }

  return rawValue
}
